import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import './index.css';
import { AppProviders } from '@/providers/AppProviders';
import { App } from '@/App';
import { readAdminModeFromUrl } from '@/auth/adminMode';

/** True on the page B2C sends back to (the sign-in popup): its address carries state plus a code or error. */
function isSignInResponse(): boolean {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, '') || window.location.search);
  return params.has('state') && (params.has('code') || params.has('error'));
}

async function bootstrap() {
  // The sign-in popup lands back on this site. MSAL v5 needs that page to hand the response to the
  // window that opened it; rendering the app here instead leaves the popup on the website and the
  // sign-in waiting. So hand it over and stop — MSAL then closes the popup.
  if (isSignInResponse()) {
    const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
    await broadcastResponseToMainFrame();
    return;
  }

  // ?admin=true / ?admin=false: Fabrica admin mode for this tab, kept across the B2C round trip.
  readAdminModeFromUrl();

  // The mock layer is dev-only. Guarding on DEV lets the bundler drop the
  // dynamic import entirely from production builds.
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_MOCKS === 'true') {
    const { enableMocks } = await import('@/mocks');
    await enableMocks();
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('Root element #root not found');

  createRoot(container).render(
    <StrictMode>
      <AppProviders>
        <App />
      </AppProviders>
    </StrictMode>,
  );
}

void bootstrap();
